/**
 * Every account, with the impersonation and deletion controls.
 *
 * The reason field is required by the API, so the dialog cannot be dismissed
 * past it. That is deliberate friction: it makes each use a decision with a
 * sentence attached, and the sentence is what the audit log is worth reading
 * for six months later.
 */

import { useState, type FormEvent } from "react";
import { useNavigate } from "@tanstack/react-router";

import { useAdminUsers, useDeleteUser, useImpersonate } from "../../api/queries";
import type { User, UserDeleted } from "../../api/types";
import { useSession } from "../../auth/session";
import {
  Badge,
  Button,
  Card,
  ErrorNote,
  Field,
  Input,
  Loading,
  Modal,
  Textarea,
} from "../../ui/primitives";

export function AdminUsersPage() {
  const users = useAdminUsers();
  const session = useSession();
  const [target, setTarget] = useState<User | null>(null);
  const [doomed, setDoomed] = useState<User | null>(null);
  const [deleted, setDeleted] = useState<UserDeleted | null>(null);

  if (users.isLoading) return <Loading />;
  if (users.error) return <ErrorNote error={users.error} />;

  return (
    <div className="space-y-4">
      {deleted && (
        <p role="status" className="rounded-lg border border-line bg-raised px-3 py-2 text-sm">
          Deleted <strong>{deleted.email}</strong>
          {deleted.organizations.length > 0
            ? `, with ${deleted.organizations.join(", ")} and ${deleted.sites} ${
                deleted.sites === 1 ? "site" : "sites"
              }.`
            : ". Their organizations had other owners and were kept."}
        </p>
      )}

      <Card>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-line text-left text-muted">
              <th scope="col" className="py-2 font-medium">Email</th>
              <th scope="col" className="font-medium">Status</th>
              <th scope="col" className="sr-only">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {(users.data ?? []).map((user) => (
              <tr key={user.id}>
                <td className="py-2">{user.email}</td>
                <td className="space-x-1">
                  {user.is_superuser && <Badge tone="brand">Staff</Badge>}
                  {user.is_active ? (
                    <Badge tone="good">Active</Badge>
                  ) : (
                    <Badge tone="bad">Disabled</Badge>
                  )}
                </td>
                <td className="space-x-1 py-2 text-right">
                  <Button
                    size="sm"
                    variant="ghost"
                    // Staff accounts cannot be impersonated — the API refuses
                    // it, so offering the button would only produce a 403.
                    disabled={user.is_superuser || !user.is_active}
                    onClick={() => setTarget(user)}
                  >
                    Impersonate
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    // Same refusals as the API: staff, and yourself.
                    disabled={user.is_superuser || user.id === session.user?.id}
                    onClick={() => setDoomed(user)}
                    aria-label={`Delete ${user.email}`}
                  >
                    Delete
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <ImpersonateDialog target={target} onClose={() => setTarget(null)} />
      <DeleteDialog
        target={doomed}
        onClose={() => setDoomed(null)}
        onDeleted={(result) => {
          setDeleted(result);
          setDoomed(null);
        }}
      />
    </div>
  );
}

function ImpersonateDialog({
  target,
  onClose,
}: {
  target: User | null;
  onClose: () => void;
}) {
  const session = useSession();
  const navigate = useNavigate();
  const impersonate = useImpersonate();
  const [reason, setReason] = useState("");

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!target) return;

    const result = await impersonate.mutateAsync({ userId: target.id, reason });
    await session.impersonate(result.access_token, result.email);
    setReason("");
    onClose();
    await navigate({ to: "/sites" });
  }

  return (
    <Modal
      open={target !== null}
      onClose={onClose}
      title={`Sign in as ${target?.email ?? ""}`}
    >
      <form onSubmit={submit} className="space-y-4">
        {impersonate.error != null && <ErrorNote error={impersonate.error} />}

        <p className="text-sm text-muted">
          You will see this customer&rsquo;s dashboard exactly as they do. This
          is recorded in the audit log with your name, the reason below and the
          time.
        </p>

        <Field
          label="Reason"
          id="reason"
          hint="What are you investigating? Stored permanently."
        >
          <Textarea
            id="reason"
            rows={2}
            required
            minLength={3}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </Field>

        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={impersonate.isPending}>
            {impersonate.isPending ? "Starting…" : "Start"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Typing the address is the confirmation. A plain "Are you sure?" gets
 * clicked through by habit, and this deletes a customer's stores, catalogue
 * and transcripts with no undo.
 */
function DeleteDialog({
  target,
  onClose,
  onDeleted,
}: {
  target: User | null;
  onClose: () => void;
  onDeleted: (result: UserDeleted) => void;
}) {
  const remove = useDeleteUser();
  const [typed, setTyped] = useState("");

  const matches = target !== null && typed.trim().toLowerCase() === target.email.toLowerCase();

  function close() {
    setTyped("");
    remove.reset();
    onClose();
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!target || !matches) return;

    const result = await remove.mutateAsync(target.id);
    setTyped("");
    onDeleted(result);
  }

  return (
    <Modal open={target !== null} onClose={close} title={`Delete ${target?.email ?? ""}`}>
      <form onSubmit={submit} className="space-y-4">
        {remove.error != null && <ErrorNote error={remove.error} />}

        <p className="text-sm text-muted">
          This permanently deletes the account and every organization it is the only
          owner of — sites, synced catalogue, conversations and Q&amp;A answers. Any
          WordPress plugin paired to those sites stops working. It cannot be undone.
        </p>

        <Field label={`Type ${target?.email ?? "the email"} to confirm`} id="confirm-email">
          <Input
            id="confirm-email"
            autoComplete="off"
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
          />
        </Field>

        <div className="flex justify-end gap-2">
          <Button onClick={close}>Cancel</Button>
          <Button type="submit" variant="danger" disabled={!matches || remove.isPending}>
            {remove.isPending ? "Deleting…" : "Delete permanently"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
